import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Table, Spin, Alert, Button, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { AutoSizer } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import {
  fetchLoadSheetPreview,
  setLoadSheetPreviewData,
  clearLoadSheetPreviewData,
  fetchLoadSheetTemplate,
  fetchLoadSheetPreviewEBPColumns,
} from '../../../actions/load_sheets/fetch';
import LoadSheetEBPPreviewPage from './LoadSheetEBPPreviewPage';
import { fetchExportBuilderAdvancedColumns } from '../../../actions/brand/export_builder_advanced/fetch';
import { resetExportBuilderAdvanced } from '../../../actions/brand/export_builder_advanced/update';
import { ApplicationState } from '../../../reducers';
import { LoadSheet } from '../../../../types/loadSheets';

const { Title } = Typography;

interface LoadSheetFilePreviewDrawerProps {
  visible: boolean;
  receiver: boolean;
  templateName: string | null;
  templateFileName?: string | null;
  isPreviewById?: boolean;
  selectedTemplateId?: number | null;
  selectedLoadSheet?: LoadSheet;
  onClose: () => void;
}

const LoadSheetFilePreviewDrawer: React.FC<LoadSheetFilePreviewDrawerProps> = ({
  visible,
  receiver,
  onClose,
  templateName,
  templateFileName,
  isPreviewById = false,
  selectedTemplateId,
  selectedLoadSheet,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const previewData = useSelector((state: ApplicationState) => state.loadSheets.previewData);
  const fetchingPreview = useSelector(
    (state: ApplicationState) => state.loadSheets.fetchingPreview
  );
  const ebpPreviewData = useSelector(
    (state: ApplicationState) => state.brand.exportBuilderAdvanced.preview
  );
  const ebpTemplateColumns = useSelector((state: ApplicationState) =>
    receiver
      ? state.brand.exportBuilderAdvanced.templateColumns
      : state.loadSheets.ebpPreviewColumns
  );
  const isLoadingEbpPreview = useSelector((state: ApplicationState) =>
    receiver
      ? state.brand.exportBuilderAdvanced.fetchingColumns
      : state.loadSheets.fetchingEbpColumns
  );
  const error = useSelector((state: ApplicationState) => state.loadSheets.error);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [previewError, setPreviewError] = useState<string | null>(null);

  const fetchExcelPreview = useCallback(async () => {
    if (!templateName) return;

    let identifier: string | number = templateName;
    if (isPreviewById) {
      identifier = parseInt(templateName, 10);
    } else {
      const parentCodeFileName = templateName.split('/');
      identifier = parentCodeFileName[parentCodeFileName.length - 1];
    }

    const response = (await dispatch(fetchLoadSheetPreview(identifier, isPreviewById))) as any;
    const responseData = response?.payload?.data || response?.data || response?.value?.data;

    if (!responseData) {
      throw new Error(t('loadSheets:loadSheetPreviewFileNoData'));
    }

    if (!(responseData instanceof ArrayBuffer) && !(responseData instanceof Uint8Array)) {
      throw new Error(t('loadSheets:loadSheetPreviewInvalidData'));
    }

    const workbook = XLSX.read(responseData, { type: 'array' });
    const sheetNames = workbook.SheetNames;

    if (sheetNames.length === 0) {
      throw new Error(t('loadSheets:loadSheetPreviewSheetNotFound'));
    }

    const firstSheetName = sheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length === 0) {
      throw new Error(t('loadSheets:loadSheetPreviewEmptySheet'));
    }

    const headers = jsonData[0] as string[];
    const columns = headers.map((header, index) => ({
      title: header || `Column ${index + 1}`,
      dataIndex: `col_${index}`,
      key: `col_${index}`,
      ellipsis: true,
      width: 150,
    }));

    const dataSource = (jsonData.slice(1) as any[][]).map((row, rowIndex) => {
      const rowData: any = { key: rowIndex };
      headers.forEach((_, colIndex) => {
        rowData[`col_${colIndex}`] = row[colIndex] || '';
      });
      return rowData;
    });

    const blob = new Blob([responseData as ArrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    setDownloadUrl(url);

    dispatch(
      setLoadSheetPreviewData({
        columns,
        dataSource,
        sheetNames,
        activeSheet: firstSheetName,
        rawData: responseData,
      })
    );

    setPagination(prev => ({
      ...prev,
      total: Math.min(dataSource.length, 100),
      current: 1,
    }));
  }, [templateName, isPreviewById, dispatch, t]);

  const fetchEBPPreviewData = useCallback(
    (templateId: number) => {
      setPreviewError(null);
      if (receiver) {
        dispatch(fetchExportBuilderAdvancedColumns(templateId));
      } else {
        dispatch(fetchLoadSheetPreviewEBPColumns(templateId));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (visible && templateName) {
      if (selectedLoadSheet?.ebp_id) {
        fetchEBPPreviewData(selectedLoadSheet.ebp_id);
      } else {
        fetchExcelPreview();
      }
    }
  }, [visible, templateName, selectedLoadSheet?.ebp_id, fetchEBPPreviewData, fetchExcelPreview]);

  useEffect(() => {
    if (selectedTemplateId && receiver) {
      dispatch(fetchLoadSheetTemplate(selectedTemplateId));
    }
  }, [selectedTemplateId, dispatch]);

  const handleDownload = () => {
    if (previewData?.rawData && (templateName || templateFileName)) {
      const blob = new Blob([previewData.rawData as ArrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      let filename: string;
      if (isPreviewById && templateFileName) {
        filename = templateFileName;
      } else if (templateName) {
        filename = templateName.split('/').pop() || 'template';
      } else {
        filename = 'template';
      }

      const downloadName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const renderFilePreviews = () => {
    return (
      <div className="load-sheet__file-preview-table">
        {fetchingPreview && (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>{t('loadSheets:loadingPreview')}</div>
          </div>
        )}

        {error && (
          <Alert
            message={t('loadSheets:previewError')}
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {previewData && !fetchingPreview && !error && (
          <AutoSizer>
            {({ height, width }) => (
              <div style={{ height, width }}>
                <Table
                  className="load-sheet-file-preview-table"
                  columns={previewData.columns}
                  dataSource={previewData.dataSource.slice(0, 100)}
                  scroll={{
                    x: width > 1057 ? undefined : 1057,
                    y: height > previewData.dataSource.length * 57 + 83 ? undefined : height - 83,
                  }}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} ${t('loadSheets:of')} ${total} ${t(
                        'loadSheets:items'
                      )}`,
                    onChange: handlePaginationChange,
                    onShowSizeChange: handlePaginationChange,
                  }}
                  size="small"
                  bordered
                  style={{
                    background: '#fff',
                    borderRadius: '6px',
                  }}
                />
              </div>
            )}
          </AutoSizer>
        )}
      </div>
    );
  };

  const renderEBPPreviews = () => {
    return (
      <LoadSheetEBPPreviewPage
        templateId={selectedLoadSheet?.ebp_id}
        ebpTemplateColumns={ebpTemplateColumns}
        isLoadingEbpPreview={isLoadingEbpPreview}
        previewError={previewError}
      />
    );
  };

  const handleClose = () => {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
    }
    dispatch(clearLoadSheetPreviewData());
    dispatch(resetExportBuilderAdvanced());
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });

    setPreviewError(null);
    onClose();
  };

  return (
    <Drawer
      title={
        !isPreviewById ? (
          <div className="flex items-center justify-between">
            <Title level={4} style={{ margin: 0 }}>
              {t('loadSheets:loadSheetPreview')}:{' '}
              {templateName ? templateName.split('/').pop() : ''}
            </Title>
            <div>
              {downloadUrl && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  style={{ marginRight: 8 }}
                >
                  {t('loadSheets:download')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              {downloadUrl && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  style={{ marginRight: 8 }}
                >
                  {t('loadSheets:download')}
                </Button>
              )}
            </div>
          </div>
        )
      }
      placement="right"
      width="90%"
      open={visible}
      onClose={handleClose}
      styles={{ body: { padding: '24px' } }}
    >
      {selectedLoadSheet?.ebp_id ? renderEBPPreviews() : renderFilePreviews()}
    </Drawer>
  );
};

export default LoadSheetFilePreviewDrawer;
